import { Component } from '@theme/component';
import { ThemeEvents, QuantitySelectorUpdateEvent } from '@theme/events';
import { morph } from '@theme/morph';
import { onAnimationEnd } from '@theme/utilities';
import { StandardEvents, ProductSelectEvent, CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';

/**
 * @typedef {Object} ProductVariant
 * @property {string|number} [id] - Variant ID
 * @property {string} [title] - Variant title
 * @property {string} [name] - Variant name
 * @property {boolean} [available] - Whether variant is available
 * @property {Object} [featured_media] - Featured media object
 * @property {Object} [featured_media.preview_image] - Preview image data
 * @property {string} [featured_media.preview_image.src] - Image source URL
 * @property {string} [featured_media.alt] - Alt text for the image
 */

/**
 * @typedef {HTMLElement & {
 *   source: Element,
 *   destination: Element,
 *   useSourceSize: string | boolean
 * }} FlyToCart
 */

/**
 * @typedef {Object} StickyAddToCartRefs
 * @property {HTMLElement} stickyBar - The floating bar container
 * @property {HTMLButtonElement} addToCartButton - Sticky bar's button
 * @property {HTMLElement} quantityDisplay - Quantity display container
 * @property {HTMLElement} quantityNumber - Quantity number element
 * @property {HTMLImageElement} productImage - Product image element
 */

/**
 * A custom element that manages a sticky add-to-cart bar.
 * Shows when the main buy buttons scroll out of view.
 *
 * GIVINO (2026-10-15), three departures from stock Horizon:
 *   - It no longer stands down for Shopify Inbox. Stock Horizon hides the bar whenever
 *     <shopify-chat> is registered, and Inbox is always installed here, so on a phone the bar never
 *     showed (measured on live: Add at 1,421px on a 390px screen, no bar at any scroll). The chat
 *     bubble is hidden on product pages instead (layout/theme.liquid), so nothing shares the
 *     bottom of the screen with the bar.
 *   - On a phone it also shows BEFORE the shopper reaches the buy buttons, while they are still
 *     below the screen, so the page opens with a way to buy in reach. It steps aside when they
 *     come into view, and for the footer, as before. Not on a wider screen: there the bar is a
 *     card floating over the middle of the page, and on a short laptop (1366 by 657) it sat on top
 *     of the purchase options it would add (design QA, 2026-10-15).
 *   - It names the chosen delivery frequency under the title ("Every 4 weeks"), read from the
 *     purchase options pills, because its button adds whatever those pills say.
 *
 * @extends {Component<StickyAddToCartRefs>}
 */
/** The strip at the foot of the screen the bar covers; the Add button counts as seen above it. */
const BAR_CLEARANCE = 64;

/** Where the bar also shows ahead of the buy buttons: the width at which it is a full-width bar. */
const PHONE_QUERY = '(max-width: 749px)';

/** Dispatched on the section by assets/givino-purchase-options.js when the choice changes. */
const PURCHASE_OPTIONS_CHANGE = 'purchase-options:change';

class StickyAddToCartComponent extends Component {
  requiredRefs = ['stickyBar', 'addToCartButton', 'quantityDisplay', 'quantityNumber'];

  /** @type {IntersectionObserver | null} */
  #buyButtonsIntersectionObserver = null;

  /** @type {IntersectionObserver | null} */
  #mainBottomObserver = null;

  /** @type {number | undefined} */
  #resetTimeout;

  /** True between a sticky press and the cart event that press produces. @type {boolean} */
  #awaitingAdd = false;

  /** @type {boolean} */
  #isStuck = false;

  /** @type {number | null} */
  #animationTimeout = null;

  /** @type {AbortController} */
  #abortController = new AbortController();

  /** @type {HTMLButtonElement | null} */
  #targetAddToCartButton = null;

  /** @type {number} */
  #currentQuantity = 1;

  /** @type {boolean} */
  #hiddenByBottom = false;

  /**
   * Whether the footer is in view, kept by the footer observer. GIVINO: the footer observer only
   * hides the bar on a TRANSITION into view, so on a page short enough that the footer is already
   * showing when the buy buttons scroll away (a ticketed tasting at 390), the bar appeared over the
   * footer and stayed there, covering its last row. The bar now never shows while this is true.
   * @type {boolean}
   */
  #footerInView = false;

  connectedCallback() {
    super.connectedCallback();

    this.#setupIntersectionObserver();

    const { signal } = this.#abortController;
    const target = this.closest('.shopify-section');
    target?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect, { signal });

    document.addEventListener(StandardEvents.cartLinesUpdate, this.#handleCartAddComplete, { signal });
    document.addEventListener(StandardEvents.cartError, this.#handleCartAddComplete, { signal });
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#handleQuantityUpdate, { signal });

    this.#getInitialQuantity();

    target?.addEventListener(PURCHASE_OPTIONS_CHANGE, this.#syncPlan, { signal });
    this.#syncPlan();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#buyButtonsIntersectionObserver?.disconnect();
    this.#mainBottomObserver?.disconnect();
    this.#abortController.abort();
    if (this.#animationTimeout) {
      clearTimeout(this.#animationTimeout);
    }
  }

  /**
   * Sets up the IntersectionObserver to watch the buy buttons visibility
   */
  #setupIntersectionObserver() {
    const productForm = this.#getProductForm();
    if (!productForm) return;

    const buyButtonsBlock = productForm.closest('.buy-buttons-block');
    if (!buyButtonsBlock) return;
    // GIVINO: the Add button itself, not the block around it. The block starts with the quantity
    // stepper, so with its top few pixels on screen the bar stood down while Add was still under
    // the fold (gin at 390 by 844: block from 836px, button from 894px, 2026-10-15).
    const watched = productForm.querySelector('[ref="addToCartButton"]') ?? buyButtonsBlock;

    // In themes migrated from 2.0, the footer element doesn't exist
    const footer = document.querySelector('footer') ?? document.querySelector('[class*="footer-group"]');
    if (!footer) return;

    // Observer for buy buttons visibility
    this.#buyButtonsIntersectionObserver = new IntersectionObserver((entries) => {
      const [entry] = entries;
      if (!entry) return;

      // Show once the buy buttons have been scrolled past, or on a phone while they are still
      // below the screen (see the class comment).
      if (!entry.isIntersecting && !this.#isStuck) {
        if (this.#buttonsOffScreen(entry.target)) {
          if (this.#footerInView) {
            // Let the footer observer show it once the footer scrolls back out of view.
            this.#hiddenByBottom = true;
            return;
          }
          this.#showStickyBar();
        }
      } else if (entry.isIntersecting && this.#isStuck) {
        this.#hiddenByBottom = false;
        this.#hideStickyBar();
      }
    }, { rootMargin: `0px 0px -${BAR_CLEARANCE}px 0px` });

    // Observer for footer visibility - hides sticky bar at page bottom
    this.#mainBottomObserver = new IntersectionObserver(
      (entries) => {
        // The latest record is the current state; a fast scroll can batch an exit and an entry.
        const entry = entries[entries.length - 1];
        if (!entry) return;
        this.#footerInView = entry.isIntersecting;

        if (entry.isIntersecting && this.#isStuck) {
          this.#hiddenByBottom = true;
          this.#hideStickyBar();
        } else if (!entry.isIntersecting && this.#hiddenByBottom) {
          // Footer out of view - check if we should show sticky bar again
          const rect = watched.getBoundingClientRect();
          if (this.#buttonsOffScreen(watched, rect)) {
            this.#hiddenByBottom = false;
            this.#showStickyBar();
          }
        }
      },
      {
        rootMargin: '200px 0px 0px 0px',
      }
    );

    this.#buyButtonsIntersectionObserver.observe(watched);
    this.#mainBottomObserver.observe(footer);
    this.#targetAddToCartButton = productForm.querySelector('[ref="addToCartButton"]');
  }

  // Public action handlers
  /**
   * Handles the add to cart button click in the sticky bar
   */
  handleAddToCartClick = () => {
    if (!this.#targetAddToCartButton) return;
    if (this.#targetAddToCartButton.getAttribute('aria-busy') === 'true') return;
    this.#targetAddToCartButton.dataset.puppet = 'true';
    // Set before the click: the form dispatches its cart event synchronously inside click().
    this.#awaitingAdd = true;
    this.#targetAddToCartButton.click();
    // Validation (a quantity over the maximum) can stop the add before anything is sent.
    if (this.#targetAddToCartButton.getAttribute('aria-busy') !== 'true') {
      this.#awaitingAdd = false;
      return;
    }
    this.#setBusy(true);
  };

  /**
   * Mirrors the main button's in-flight state on the sticky bar's button.
   * @param {boolean} busy
   */
  #setBusy(busy) {
    const button = this.refs.addToCartButton;
    button.classList.toggle('is-busy', busy);
    if (busy) {
      button.setAttribute('aria-busy', 'true');
      button.setAttribute('aria-disabled', 'true');
    } else {
      button.removeAttribute('aria-busy');
      button.removeAttribute('aria-disabled');
    }
  }

  /**
   * The checkmark and fly-to-cart, played once Shopify has confirmed the add. Until 2026-10-04
   * they played on the press, before the request had even returned.
   */
  #playAdded = async () => {
    const cartIcon = document.querySelector('.header-actions__cart-icon');

    if (this.refs.addToCartButton.dataset.added !== 'true') {
      this.refs.addToCartButton.dataset.added = 'true';
    }

    if (!cartIcon || !this.refs.addToCartButton || !this.refs.productImage) return;
    if (this.#resetTimeout) clearTimeout(this.#resetTimeout);

    const flyToCartElement = /** @type {FlyToCart} */ (document.createElement('fly-to-cart'));
    const sourceStyles = getComputedStyle(this.refs.productImage);

    flyToCartElement.classList.add('fly-to-cart--sticky');
    flyToCartElement.style.setProperty('background-image', `url(${this.refs.productImage.src})`);
    flyToCartElement.useSourceSize = 'true';
    flyToCartElement.source = this.refs.productImage;
    flyToCartElement.destination = cartIcon;

    document.body.appendChild(flyToCartElement);

    await onAnimationEnd([this.refs.addToCartButton, flyToCartElement]);
    this.#resetTimeout = setTimeout(() => {
      this.refs.addToCartButton.removeAttribute('data-added');
    }, 800);
  };

  /**
   * Handles product select events (variant selected and updated)
   * @param {ProductSelectEvent} event - The product select event
   */
  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    // Update variant ID from the event detail (variant:selected part)
    const { optionValueId } = event.detail ?? {};
    if (optionValueId) {
      this.dataset.currentVariantId = optionValueId;
    }

    // Wait for the promise to resolve with variant update data
    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;

        const { html, productId, resource: variant } = detail;

        if (productId && productId !== this.dataset.productId) return;

        // Get the new sticky add to cart HTML from the server response
        const newStickyAddToCart = /** @type {HTMLElement | null} */ (html.querySelector('sticky-add-to-cart'));
        if (!newStickyAddToCart) return;

        const newStickyBar = newStickyAddToCart.querySelector('[ref="stickyBar"]');
        if (!newStickyBar) return;

        // Store current visibility state before morphing
        const currentStuck = this.refs.stickyBar.getAttribute('data-stuck') || 'false';
        const variantAvailable = newStickyAddToCart.dataset.variantAvailable;

        // Morph the entire sticky bar content
        morph(this.refs.stickyBar, newStickyBar, { childrenOnly: true });

        // Restore visibility state after morphing
        this.refs.stickyBar.setAttribute('data-stuck', currentStuck);
        this.dataset.variantAvailable = variantAvailable;

        // Update the dataset attributes with new variant info
        if (variant && variant.id) {
          this.dataset.currentVariantId = variant.id;
        }

        // Re-cache the target add to cart button after morphing
        const productForm = this.#getProductForm();
        if (productForm) {
          this.#targetAddToCartButton = productForm.querySelector('[ref="addToCartButton"]');
        }

        if (variant == null) {
          this.#handleVariantUnavailable();
        }
        // Restore the current quantity display if needed
        this.#updateButtonText();
        // The morph put the server's empty plan line back.
        this.#syncPlan();
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[sticky-add-to-cart] Event promise rejected:', error);
      });
  };

  /**
   * Updates the variant title based on selected options when the variant is unavailable
   */
  #handleVariantUnavailable = () => {
    this.dataset.currentVariantId = '';
    const variantTitleElement = this.querySelector('.sticky-add-to-cart__variant');
    const productId = this.dataset.productId;
    const variantPicker = document.querySelector(`variant-picker[data-product-id="${productId}"]`);
    if (!variantTitleElement || !variantPicker) return;

    const selectedOptions = Array.from(variantPicker.querySelectorAll('input:checked'))
      .map((option) => /** @type {HTMLInputElement} */ (option).value)
      .filter((value) => value !== '')
      .join(' / ');
    if (!selectedOptions) return;
    variantTitleElement.textContent = selectedOptions;
  };

  /**
   * Handles cart add complete (success or error) - resets puppet flag
   * @param {CartLinesUpdateEvent | CartErrorEvent} event - The cart event
   */
  #handleCartAddComplete = (event) => {
    // Reset the puppet flag only after the cart operation's promise settles,
    // not when the event is first dispatched (before the HTTP request completes).
    const resetPuppet = () => {
      if (this.#targetAddToCartButton) {
        this.#targetAddToCartButton.dataset.puppet = 'false';
      }
    };

    // The first cart event after a sticky press is that press's add. Its promise resolves with
    // didError on a refused add (out of stock, over the limit) and rejects on a network failure.
    if (this.#awaitingAdd && 'promise' in event && event.promise instanceof Promise) {
      this.#awaitingAdd = false;
      event.promise.then(
        (result) => {
          this.#setBusy(false);
          if (!result?.detail?.didError) this.#playAdded();
        },
        () => this.#setBusy(false)
      );
    }

    // CartLinesUpdateEvent has a promise; CartErrorEvent does not (error already happened).
    if ('promise' in event && event.promise instanceof Promise) {
      event.promise.finally(resetPuppet);
    } else {
      resetPuppet();
    }
  };

  /**
   * Handles quantity selector update events
   * @param {QuantitySelectorUpdateEvent} event - The quantity update event
   */
  #handleQuantityUpdate = (event) => {
    // Only respond to product page quantity selector updates, not cart drawer
    if (event.detail.cartLine) return;

    this.#currentQuantity = event.detail.quantity;
    this.#updateButtonText();
  };

  /**
   * Shows the sticky bar with animation
   */
  #showStickyBar() {
    const { stickyBar } = this.refs;
    this.#isStuck = true;
    stickyBar.dataset.stuck = 'true';
  }

  /**
   * Hides the sticky bar with animation
   */
  #hideStickyBar() {
    const { stickyBar } = this.refs;
    this.#isStuck = false;
    stickyBar.dataset.stuck = 'false';
  }

  /**
   * Whether the Add button is off screen in a way that calls for the bar: scrolled past at any
   * width, or on a phone not yet clear of the bottom strip the bar covers. The same strip as the
   * observer's root margin, or the two disagree and the bar fails to come back on scrolling up.
   * @param {Element} buyButtons
   * @param {DOMRect} [rect]
   * @returns {boolean}
   */
  #buttonsOffScreen(buyButtons, rect = buyButtons.getBoundingClientRect()) {
    if (rect.bottom < 0 || rect.top < 0) return true;
    return rect.top >= window.innerHeight - BAR_CLEARANCE && window.matchMedia(PHONE_QUERY).matches;
  }

  /** The chosen delivery frequency under the title, or nothing for a one-off. */
  #syncPlan = () => {
    const line = this.querySelector('.sticky-add-to-cart__plan');
    if (!(line instanceof HTMLElement)) return;
    const chosen = this.closest('.shopify-section')?.querySelector('purchase-options input[type="radio"]:checked');
    const label = chosen instanceof HTMLInputElement && chosen.value ? chosen.closest('label')?.textContent?.trim() ?? '' : '';
    line.textContent = label;
    line.hidden = label === '';
    // The bar's button says only "Add to cart"; this tells a screen reader which plan it adds.
    line.id ||= `sticky-plan-${this.dataset.productId}`;
    const button = this.refs.addToCartButton;
    if (label) button.setAttribute('aria-describedby', line.id);
    else button.removeAttribute('aria-describedby');
  };

  // Helper methods
  /**
   * Gets the product form element
   * @returns {HTMLElement | null}
   */
  #getProductForm() {
    const productId = this.dataset.productId;
    if (!productId) return null;

    const sectionElement = this.closest('.shopify-section');
    if (!sectionElement) return null;

    const sectionId = sectionElement.id.replace('shopify-section-', '');
    return document.querySelector(
      `#shopify-section-${sectionId} product-form-component[data-product-id="${productId}"]`
    );
  }

  /**
   * Gets the initial quantity from the data attribute
   */
  #getInitialQuantity() {
    this.#currentQuantity = parseInt(this.dataset.initialQuantity || '1') || 1;
    this.#updateButtonText();
  }

  /**
   * Updates the button text to include quantity
   */
  #updateButtonText() {
    const { addToCartButton, quantityDisplay, quantityNumber } = this.refs;

    const available = !addToCartButton.disabled;

    // Update the quantity number
    quantityNumber.textContent = this.#currentQuantity.toString();

    // Show/hide the quantity display based on availability and quantity
    if (available && this.#currentQuantity > 1) {
      quantityDisplay.style.display = 'inline';
    } else {
      quantityDisplay.style.display = 'none';
    }
  }
}

if (!customElements.get('sticky-add-to-cart')) {
  customElements.define('sticky-add-to-cart', StickyAddToCartComponent);
}
